import { onlyText } from 'react-children-utilities';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Button,
  IconProps,
  PopoverMenu,
  PopoverMenuItem,
  PopoverMenuItemDropdown,
  type PopoverMenuItemDropdownSubItemProps,
  usePopoverMenu,
} from '@frontend/design-system';

export type Action = {
  id: string;
  hide?: boolean;
  disabled?: boolean;
  Icon: React.FC<React.PropsWithChildren<IconProps>>;
  label: React.ReactNode;
  hoverLabel?: string;
  trackingId?: string;
  destructive?: boolean;
  onClick?: () => void;
  subMenuItems?: (props: PopoverMenuItemDropdownSubItemProps) => React.ReactNode;
};

type CustomActionCellProps = {
  actions: Action[];
  tableTrackingId: string;
  iconSize?: 'small' | 'large';
};

export const CustomActionCell = ({ actions, tableTrackingId, iconSize }: CustomActionCellProps) => {
  const { getTriggerProps, getMenuProps, getItemProps, close } = usePopoverMenu({
    interactionOptions: {
      listNavigation: {
        openOnArrowKeyDown: false,
      },
    },
    placement: 'left',
  });

  const triggerProps = getTriggerProps();
  const menuProps = getMenuProps();

  const hiddenActions = () => {
    if (actions) {
      return actions?.reduce((acc, { hide }) => {
        if (hide) {
          return acc + 1;
        }
        return acc;
      }, 0);
    }
    return 0;
  };

  const hasAllActionsHidden = hiddenActions() === actions?.length;

  const showSingleAction = actions
    ? (actions.length === 1 && !hasAllActionsHidden) || actions.length - hiddenActions() === 1
    : false;

  const renderActions = (
    getItemPropsFunction: ReturnType<typeof usePopoverMenu>['getItemProps'],
    showSingleButton?: boolean
  ) => {
    const renderPopoverItem = (
      action: Action,
      itemProps: {
        onClick: () => void;
        index: number;
      },
      id: number
    ) => {
      const { onClick, Icon: ActionIcon, hide, label, disabled, trackingId, subMenuItems, ...dynamicProps } = action;

      if (hide) return null;

      if (subMenuItems) {
        return (
          <PopoverMenuItemDropdown
            {...itemProps}
            {...dynamicProps}
            css={disabled && { pointerEvents: 'auto' }}
            key={id}
            Icon={ActionIcon}
            label={onlyText(label as React.ReactNode)}
            disabled={disabled}
            trackingId={trackingId}
            subMenuItems={subMenuItems}
            showOnHover
            closeParentMenu={close}
          >
            {label}
          </PopoverMenuItemDropdown>
        );
      }

      return (
        <PopoverMenuItem
          {...itemProps}
          {...dynamicProps}
          css={disabled && { pointerEvents: 'auto' }}
          key={id}
          Icon={ActionIcon}
          label={onlyText(label as React.ReactNode)}
          disabled={disabled}
          trackingId={trackingId}
        >
          {label ?? ''}
        </PopoverMenuItem>
      );
    };

    const renderSingleButton = (action: Action, id: number) => {
      const { onClick, Icon: ActionIcon, hide, label, disabled, trackingId, ...dynamicProps } = action;

      if (hide) return null;

      return (
        <Button
          variant='secondary'
          css={{
            padding: theme.spacing(0.5),
            border: 'none',
          }}
          {...dynamicProps}
          key={id}
          onClick={() => {
            onClick?.();
          }}
          disabled={disabled}
          label={onlyText(label as React.ReactNode)}
          trackingId={trackingId}
        >
          <ActionIcon color='light' size={24} />
        </Button>
      );
    };

    return actions?.map((action, id) => {
      const itemProps = getItemPropsFunction?.({
        onClick: () => {
          action.onClick?.();
          close();
        },
        index: id,
      });

      if (showSingleButton) {
        return renderSingleButton(action, id);
      }

      return renderPopoverItem(action, itemProps, id);
    });
  };

  return !showSingleAction ? (
    <Button
      variant='secondary'
      css={{
        padding: theme.spacing(0.5),
        border: 'none',
        height: iconSize === 'small' ? 24 : 32,
      }}
      label={'Action menu'}
      disabled={hasAllActionsHidden}
      trackingId={`${tableTrackingId}-${'icn-tablerowactionmenu'}`}
      {...triggerProps}
    >
      <Icon name='more' color='light' size={iconSize === 'small' ? 16 : 24} />
      <PopoverMenu {...menuProps} title={''} css={{ maxWidth: 320 }} returnFocus={false}>
        {renderActions(getItemProps)}
      </PopoverMenu>
    </Button>
  ) : (
    <>{renderActions(getItemProps, true)}</>
  );
};
